/**
 * Component Name: PODetailsTable
 * Description: A table component that displays purchase order details for a specific project or stage, with sortable columns and clickable PO numbers.
 * Props:
 *   - filteredInvoices (array): List of filtered invoice objects to group by purchase orders.
 *   - projectId (string): The ID of the selected project.
 *   - stageId (string, optional): The ID of the selected stage or 'variations' for variation invoices.
 *   - handlePOClick (function): Callback to handle clicks on PO numbers for drill-down.
 *   - projects (array): List of project objects for mapping project IDs to names.
 *   - breadcrumbItems (array): List of breadcrumb items for navigation.
 *   - handleBreadcrumbClick (function): Callback for handling breadcrumb clicks.
 * Feature: Groups invoices by purchase orders for a selected project or stage, displays details like procurement code, vendor, and invoice amounts, and supports navigation via breadcrumbs and clickable PO numbers in the invoice management interface.
 */
import React from 'react';
import Breadcrumb from 'react-bootstrap/Breadcrumb';
import CustomTable from '../table/table';
import { toUSD } from '../../util/currency-formatter';
import { toDecimal, roundValue } from '../../util/decimal-utils';
import idx from 'idx';
import { format } from 'date-fns';
import { INVOICE_CSS_CLASSES, INVOICE_ERROR_MESSAGES, INVOICE_STATUS, INVOICE_TABLE_COLUMNS } from '../../util/enums/invoice-management';
const PODetailsTable = ({ filteredInvoices, projectId, stageId = '', handlePOClick, projects, breadcrumbItems, handleBreadcrumbClick }) => {
    // Filter invoices for the selected project and group by PurchaseOrders
    const poData = [];
    const poMap = new Map();
    filteredInvoices
        .filter((inv) =>
            stageId === 'variations' ? inv.stageId === stageId && inv.projectId === projectId :
                stageId ? inv.stageId === stageId : inv.projectId === projectId
        )
        .forEach((inv) => {
            const poId = idx(inv, _ => inv.PurchaseOrders.id) || INVOICE_ERROR_MESSAGES.NA;
            const procurementCode = idx(inv, _ => inv.PurchaseOrders.f_id) || INVOICE_ERROR_MESSAGES.NA;
            const project = projects.find((p) => p.id === inv.projectId);
            if (!poMap.has(poId)) {
                poMap.set(poId, {
                    poId,
                    procurementCode,
                    projectId: inv.projectId,
                    projectName: project?.name || INVOICE_ERROR_MESSAGES.NA,
                    sectionName: inv.sectionHierarchy || INVOICE_ERROR_MESSAGES.NA,
                    stageId: inv.stageId || INVOICE_ERROR_MESSAGES.NA,
                    totalAmount: toDecimal(0),
                    invoiceCount: 0,
                    vendor: inv.PurchaseOrders.vendor.business_name || INVOICE_ERROR_MESSAGES.NA,
                    poAddress: idx(inv, (_) => _.PurchaseOrders.address.join(', ')) || idx(inv, (_) => _.PurchaseOrders.vendor.address.replace(/ /g, ', ')) || INVOICE_ERROR_MESSAGES.NA,
                    poCity: inv.PurchaseOrders.city || INVOICE_ERROR_MESSAGES.NA,
                    poState: inv.PurchaseOrders.state || INVOICE_ERROR_MESSAGES.NA,
                    status: inv.PurchaseOrders.status || INVOICE_ERROR_MESSAGES.NA,
                    issueDate: inv.PurchaseOrders.createdAt ? format(new Date(inv.PurchaseOrders.createdAt), "dd/MM/yyyy") : "",
                    invoiceStatus: inv.PurchaseOrders.invoicestatus || INVOICE_ERROR_MESSAGES.NA,
                    materialstatus: inv.PurchaseOrders.materialstatus || INVOICE_ERROR_MESSAGES.NA,
                    total: inv.PurchaseOrders?.total || INVOICE_ERROR_MESSAGES.NA,
                });
            }
            const po = poMap.get(poId);
            po.totalAmount = po.totalAmount.plus(toDecimal(inv.totalAmount || 0));
            po.invoiceCount += 1;
        });

    poMap.forEach((value) => {
        value.totalAmount = roundValue(value.totalAmount);
        poData.push(value);
    });

    const columns = [
        {
            title: INVOICE_TABLE_COLUMNS.PROJECT,
            field: 'projectName',
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.PO_NUMBER,
            field: 'poId',
            render: (row) => (
                <span className={INVOICE_CSS_CLASSES.PO_LINK} onClick={() => handlePOClick(row.poId, projectId, row.stageId, row.procurementCode)}>
                    {/* procurementCode is the PO number with project prefix and Suffix if null then default to po Number*/}
                    {row.procurementCode || row.poId}
                </span>
            ),
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.SECTION_SUBSECTION,
            field: 'sectionName',
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.VENDOR,
            field: 'vendor',
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.ADDRESS,
            field: 'poAddress',
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.STATUS,
            field: 'status',
            columnSearch: true,
            render: (rowData) => <span className={rowData.status == INVOICE_STATUS.PENDING ? INVOICE_CSS_CLASSES.TEXT_WARNING : rowData.status == INVOICE_STATUS.COMPLETED ? INVOICE_CSS_CLASSES.TEXT_SUCCESS : ''}>{rowData.status}</span>,
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.ISSUE_DATE,
            field: 'issueDate',
            columnSearch: true,
            sortable: true,
            type: 'date',
        },
        {
            title: INVOICE_TABLE_COLUMNS.TOTAL,
            field: 'total',
            class: 'text-right justify-content-end',
            render: (row) => <span className={INVOICE_CSS_CLASSES.AMOUNT}>{toUSD(row.total)}</span>,
            sortable: true,
            columnSearch: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.MATERIAL_STATUS,
            field: 'materialstatus',
            columnSearch: true,
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.INVOICE_AMOUNT,
            field: 'totalAmount',
            class: 'text-right justify-content-end',
            columnSearch: true,
            render: (row) => <span className={INVOICE_CSS_CLASSES.SUB_TOTAL_AMOUNT}> {toUSD(row.totalAmount)}</span>,
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.INVOICE_COUNT,
            field: 'invoiceCount',
            class: 'text-right justify-content-end',
            columnSearch: true,
            sortable: true,
        },
        {
            title: INVOICE_TABLE_COLUMNS.INVOICE_STATUS,
            field: 'invoiceStatus',
            columnSearch: true,
            sortable: true,
        }
    ];

    const tableItemClickHandler = (row) => {
        handlePOClick(row.poId, projectId, row.stageId, row.procurementCode);
    };

    return (
        <div className="table-container">
            <Breadcrumb className="table-breadcrumb">
                {breadcrumbItems.map((item, index) => (
                    <Breadcrumb.Item
                        key={index}
                        active={item.isActive}
                        onClick={item.onClick ? () => handleBreadcrumbClick(item) : undefined}
                        className={item.onClick ? 'breadcrumb-clickable' : ''}
                    >
                        {item.label}
                    </Breadcrumb.Item>
                ))}
            </Breadcrumb>
            <CustomTable columns={columns} data={poData} pagination={true} rowClickHandler={tableItemClickHandler} />
        </div>
    );
};

export default PODetailsTable;